import React, { useMemo } from "react";
import { SkeletonText } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { fmtCount } from "../shared/format";

// Style + section assignment is name-based so the visual order can differ from the
// backend's `sort` order without breaking colors. Stages flagged as "annotation"
// are pulled out of the forward funnel and rendered under an Annotations divider —
// they're parallel signals (a quality gate, a dedup count), not pipeline stages.
// Labels and product vocabulary ("Playbook", "Opportunities Scored", "Proximity Mapped",
// "affinity", "proximity") follow the EI spec; `label` is left null so the backend
// stage name renders as-is.
const STAGE_META = [
  { match: /events\s*discovered|^discovered/i, label: null, bar: "bg-slate-400 dark:bg-slate-500", hint: "Events pulled from Ticketmaster + Google Events in your market", section: "flow" },
  { match: /proximity\s*mapped|^mapped/i, label: null, bar: "bg-sky-500", hint: "Events linked to your property with distance and proximity relevance", section: "flow" },
  { match: /opportunit.*scored|^scored/i, label: null, bar: "bg-indigo-500", hint: "Events scored on affinity, proximity, attendance, lead time, and duration", section: "flow" },
  { match: /playbook/i, label: null, bar: "bg-green-500", hint: "Events with a generated playbook (Claude marketing brief)", section: "flow" },
  { match: /score\s*50|actionable/i, label: null, bar: "bg-emerald-500", hint: "The strongest opportunities — score 50+ is the threshold for actively marketing", section: "flow" },
  { match: /cluster/i, label: null, bar: "bg-amber-500", hint: "Duplicate events merged before scoring (same event from multiple sources counted once)", section: "annotation" },
];

const DEFAULT_META = { bar: "bg-gray-400", label: null, hint: "", section: "flow" };
const metaFor = (name) => STAGE_META.find((meta) => meta.match.test(name)) ?? DEFAULT_META;

const pctOf = (num, denom) => {
  if (!denom) return 0;
  return Math.round((num / denom) * 100);
};

const StageRow = ({ stage, maxCount, firstCount, showPct }) => {
  const meta = stage.__meta ?? DEFAULT_META;
  const count = Number(stage.cnt || 0);
  const widthPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const pctOfFirst = pctOf(count, firstCount);

  return (
    <div className="grid grid-cols-[11rem_1fr_auto] items-center gap-3 max-[499px]:grid-cols-[1fr_auto] max-[499px]:gap-y-1" title={meta.hint}>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-[499px]:col-span-2 max-[499px]:!whitespace-normal max-[499px]:!overflow-visible">
        {meta.label || stage.stage}
      </span>
      <div className="relative h-3 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden min-w-0">
        <div
          className={`h-full rounded-full ${meta.bar} transition-all`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
      <div className="flex items-baseline gap-2 justify-end min-w-[4rem] md:min-w-[5rem]">
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums">
          {fmtCount(count)}
        </span>
        {showPct && firstCount > 0 && (
          <span
            title={`${pctOfFirst}% of Events Discovered (${fmtCount(firstCount)})`}
            className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums w-10 text-right cursor-help"
          >
            {pctOfFirst}%
          </span>
        )}
        {!showPct && <span className="w-10" />}
      </div>
    </div>
  );
};

const PipelineHealthPanel = ({ loading, error, rows }) => {
  const { flowStages, clusterStage, maxCount, firstCount } = useMemo(() => {
    const stages = Array.isArray(rows) ? [...rows] : [];
    stages.sort((stageA, stageB) => Number(stageA.sort || 0) - Number(stageB.sort || 0));

    const flow = [];
    let cluster = null;
    for (const row of stages) {
      const meta = metaFor(row.stage);
      const enriched = { ...row, __meta: meta };
      if (/cluster/i.test(row.stage)) cluster = enriched;
      else flow.push(enriched);
    }

    // Render in the backend's `sort` order. Playbooks are generated only for
    // Score 50+ opportunities, so the counts are naturally monotonic
    // (Score 50+ ≥ Playbooks Generated) and need no reordering.

    const max = flow.reduce((acc, stage) => Math.max(acc, Number(stage.cnt || 0)), 0);
    const first = Number(flow[0]?.cnt || 0);
    return { flowStages: flow, clusterStage: cluster, maxCount: max, firstCount: first };
  }, [rows]);

  const hasStages = flowStages.length > 0 || clusterStage;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Pipeline Health</h3>
            <InfoTip text="Where every event ends up — from first discovered, to nearby, to scored, to a generated playbook. A big drop between two stages usually means something to look into." />
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
              All-time
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            From discovery to playbook — where events fall off along the way.
          </p>
        </div>
      </div>

      {loading ? (
        <SkeletonText lines={6} lineClass="h-10" gapClass="space-y-3" />
      ) : error ? (
        <ErrorState message={error} />
      ) : !hasStages ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No pipeline data available yet.
        </div>
      ) : (
        <>
          <div className="space-y-2.5">
            {flowStages.map((stage) => (
              <StageRow
                key={stage.stage}
                stage={stage}
                maxCount={maxCount}
                firstCount={firstCount}
                showPct
              />
            ))}
          </div>

          {clusterStage && (
            <p className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
              <span className="font-semibold">Note:</span>{" "}
              {fmtCount(clusterStage.cnt)} duplicate {Number(clusterStage.cnt) === 1 ? "event was" : "events were"} merged before scoring — when the same event shows up on more than one source, we count it once.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default PipelineHealthPanel;
